import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import { LanguageContext } from '../contexts/LanguageContext';

/**
 * 構造化データ（JSON-LD）コンポーネント
 * 企業情報とサービス情報をGoogleが理解しやすい形で提供
 */
const StructuredData = () => {
  const intl = useIntl();
  const { locale } = useContext(LanguageContext);

  const toSafeJsonLd = data =>
    JSON.stringify(data)
      .replace(/</g, '\\u003c')
      .replace(/>/g, '\\u003e')
      .replace(/&/g, '\\u0026');

  // 企業情報の構造化データ
  const organizationData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name:
      locale === 'ja'
        ? '株式会社日本トレカセンター'
        : 'Japan Trading Card Center Co., Ltd.',
    alternateName: 'JTCC',
    url: 'https://jtc.center/',
    logo: 'https://jtc.center/assets/images/jp_jtc_logo_normal.svg',
    description: intl.formatMessage({
      id: locale === 'ja' ? 'meta.description.ja' : 'meta.description.en',
    }),
    foundingDate: '2023-04',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '日本橋大伝馬町13-7 日本橋大富ビル 2F',
      addressLocality: '中央区',
      addressRegion: '東京都',
      postalCode: '103-0011',
      addressCountry: 'JP',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      url: 'https://jtc.center/contact',
    },
    sameAs: [
      'https://twitter.com/jpn_tcg_center',
      'https://x.com/jtc_world',
      'https://www.wantedly.com/companies/company_5841931',
    ],
    industry: 'Trading Card Games',
    numberOfEmployees: {
      '@type': 'QuantitativeValue',
      value: '1-50',
    },
  };

  // サービス情報の構造化データ
  const servicesData = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name:
      locale === 'ja' ? 'ネットオリパサービス' : 'Online Mystery Pack Service',
    description:
      locale === 'ja'
        ? '24時間365日利用可能なオンライントレーディングカードオリパサービス'
        : '24/7 online trading card mystery pack service',
    provider: {
      '@type': 'Organization',
      name:
        locale === 'ja'
          ? '株式会社日本トレカセンター'
          : 'Japan Trading Card Center Co., Ltd.',
    },
    serviceType: 'Online Gaming Service',
    areaServed: [
      {
        '@type': 'Country',
        name: 'Japan',
      },
      {
        '@type': 'Place',
        name: 'Global',
      },
    ],
  };

  // WebSite構造化データ
  const websiteData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: intl.formatMessage({
      id: locale === 'ja' ? 'meta.title.ja' : 'meta.title.en',
    }),
    url: 'https://jtc.center/',
    description: intl.formatMessage({
      id: locale === 'ja' ? 'meta.description.ja' : 'meta.description.en',
    }),
    publisher: {
      '@type': 'Organization',
      name:
        locale === 'ja'
          ? '株式会社日本トレカセンター'
          : 'Japan Trading Card Center Co., Ltd.',
    },
    inLanguage: [
      'ja-JP',
      'en-US',
      'zh-CN',
      'zh-TW',
      'zh-HK',
      'ko-KR',
      'th-TH',
      'id-ID',
      'vi-VN',
      'es-ES',
      'fr-FR',
      'it-IT',
      'de-DE',
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: toSafeJsonLd(organizationData),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: toSafeJsonLd(servicesData),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: toSafeJsonLd(websiteData),
        }}
      />
    </>
  );
};

export default StructuredData;
